/**
 * chat-handler.js — Production chat responder.
 *
 * STAGE: Base — always loaded (after chat.js).
 *
 * RESPONSIBILITIES:
 *   Owns the BE-facing chat loop. chat.js pushes the user bubble and emits
 *   ws-chat-request; this module answers it:
 *     ensure session -> classify (get-response-type)
 *       -> stream reply (SSE stream-reply) OR show intro text
 *       -> fetch follow-up recommendations.
 *
 * SESSION:
 *   Created on first panel open (ws-toggle) and persisted per tenant in
 *   localStorage (see core/session.js). One in-flight request at a time.
 *
 * EVENTS CONSUMED:
 *   ws-chat-request { text, source }   source: 'prompt' | 'input' | 'search'
 *   ws-wine-page    { pageNo, messageId, listingMessage, ... }
 *   ws-toggle       { isOpen }
 */

import {
  on,
  emit,
  getState,
  setState,
  addMessage,
  updateMessage,
  updateChatState,
} from "../../core/state.js";
import { getTheme, getTenantId, getUiFeature } from "../../core/configAccessors.js";
import { loadSession, persistSession } from "../../core/session.js";
import * as api from "../../core/api.js";

let _sessionPromise = null;
const STREAM_DONE_SENTINEL =
  '<div style="display:none">b1cc4f43c5021eff711caa13035d233d</div>';

// BE get-response-type `type` token -> CardCarousel cardType token.
// CardCarousel resolves the renderer as Capitalize(cardType)+"Card", so
// "wine" -> WineCard, "eventDetails" -> EventDetailsCard, etc.
// Source of truth for the BE side: backend OLD_TO_NEW_CARD_TYPE_MAP +
// AdditionalCardType (apps/chat/constants.py). cart_details is handled
// separately below — it renders a panel, not a carousel.
const TYPE_TO_CARD = {
  wine_listing: "wine",
  tasting_experience: "tasting",
  membership: "membership",
  event: "event",
  reserved_event: "event",
  detailed_event_information: "eventDetails",
  reviews_information: "review",
};

const AVAILABLE_WINES_MESSAGE = "Show me all available wines";
const LATEST_OFFERS_MESSAGE = "Show me your latest offers";

const WINEENGINE_SKIP_RECO_TYPES = new Set(["wine_listing", "membership"]);

function isWineEngineTenant() {
  const id = String(getTenantId() || "").toLowerCase();
  return id === "wineengine" || id === "thewineengine";
}

function shouldSkipRecommendations(cls, userMessage = "") {
  if (!cls) return false;
  if (cls.type === "quiz") return true;
  if (cls.type === "reviews_information") return true;
  if (cls.type === "cart_details" && (cls.feature_cards?.length || cls.cart_details?.items?.length)) return true;

  const msg = String(userMessage || "").trim();
  if (msg === LATEST_OFFERS_MESSAGE) {
    return false;
  }

  if (isWineEngineTenant() && WINEENGINE_SKIP_RECO_TYPES.has(cls.type)) {
    return true;
  }
  return false;
}

function errorText() {
  return getTheme(
    "error_message",
    "Sorry, I'm unable to respond right now. Please try again shortly.",
  );
}

// Create (or restore) the session. De-dupes concurrent calls. Exported so the
// analytics module can reuse it instead of duplicating session creation.
export async function ensureSession() {
  const state = getState();
  if (state.session.id) return state.session.id;

  const tenantId = getTenantId();
  const stored = loadSession(tenantId);
  if (stored) {
    persistSession(tenantId, stored); // re-hydrate state.session.id
    return stored;
  }

  if (!_sessionPromise) {
    _sessionPromise = (async () => {
      const data = await api.personalizeChat({
        entryPoint: state.session.entryPoint ?? "",
        tableNo: state.session.tableNo ?? "",
        isQR: state.session.isQR ?? false,
      });
      const id = data?.session_id || null;
      if (id) persistSession(tenantId, id);
      return id;
    })().finally(() => {
      _sessionPromise = null;
    });
  }
  return _sessionPromise;
}

// Read the SSE stream and grow the bot bubble token-by-token.
async function streamReply(sessionId, text, buttonClicked, usedStt, isSearch = false) {
  const res = await api.getStreamingResponse({
    session_id: sessionId,
    user_message: text,
    button_clicked: buttonClicked,
    used_stt: !!usedStt,
    // Old main.js sent is_search on BOTH the classify and the stream call
    // (lines 1783 + 1805) — the BE counts the search-bar entry point on each.
    is_search: isSearch,
  });

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let answer = "";
  let done = false;
  // Create the bot bubble lazily on the first token. Until then the typing
  // indicator stays up, so the multi-second wait for the first byte shows
  // feedback instead of an empty, dead-looking bubble.
  let bubble = null;

  while (!done) {
    const { value, done: streamDone } = await reader.read();
    if (streamDone) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by a blank line.
    const frames = buffer.split("\n\n");
    buffer = frames.pop() ?? ""; // keep the trailing incomplete frame

    for (const frame of frames) {
      let payloadEnd = false;
      if (!frame.startsWith("data:")) continue;
      let payload = frame.slice(5);
      if (payload.startsWith(" ")) payload = payload.slice(1); // strip one SSE space
      if (payload === STREAM_DONE_SENTINEL) { 
        done = true; 
        payloadEnd = true; 
        if (bubble) {
          updateMessage(bubble.id, { text: answer, type: "ongoing", payloadEnd });
        }
        break; }
      if (payload === "[DONE]") { done = true; break; }
      if (payload.startsWith("[ERROR]")) { done = true; break; }
      answer += payload;
      if (!bubble) {
        emit("ws-chat-typing", { active: false }); // first content → swap dots for the reply
        bubble = addMessage({ role: "bot", kind: "text", data: { text: "" } });
      }
      updateMessage(bubble.id, { text: answer, type: "ongoing", payloadEnd });
    }
  }

  // No content at all: hide the dots and surface the error as a fresh bubble.
  if (!bubble) {
    emit("ws-chat-typing", { active: false });
    addMessage({ role: "bot", kind: "text", data: { text: errorText() } });
  } else if (!answer) {
    updateMessage(bubble.id, { text: errorText() });
  }
}

// Best-effort follow-up question + clickable options.
async function fetchRecommendations(sessionId) {
  let rec;
  emit("ws-chat-typing", { active: true });
  try {
    rec = await api.getQuestionRecommendations(sessionId);
  } catch (err) {
    // Was silently swallowed — surface it so a missing follow-up prompt list
    // is diagnosable instead of failing invisibly.
    console.warn("[RedConcierge] recommendations fetch failed", err);
    return;
  } finally {
    emit("ws-chat-typing", { active: false });
  }
  if (!rec) return;
  if (rec.continuing_question) {
    addMessage({ role: "bot", kind: "text", isFirstResponse: false, data: { text: rec.continuing_question } });
  }
  if (
    rec.give_possible_response_options &&
    Array.isArray(rec.possible_response_options) &&
    rec.possible_response_options.length
  ) {
    addMessage({
      role: "bot",
      kind: "prompts",
      isFirstResponse: false,
      data: { items: rec.possible_response_options, source: "recommendations" },
    });
  }
}

function flattenFeatureCards(featureCards) {
  // ⚠ SHARED/BASE — all tenants. Each feature card carries the authoritative
  // data in `json_data`; the BE also denormalizes some fields to the card's top
  // level for querying, but those can go STALE (verified vs prod: 7/10 Fogarty
  // wines had a wrong top-level price — e.g. Rosalee top=$0.00 vs json=$38.00 —
  // while EVERY card's json_data matched prod). So flatten json_data over the top
  // level (json_data wins for data fields; top-level-only fields like message /
  // unique_id are preserved since json_data lacks them). No-op when absent.
  return (Array.isArray(featureCards) ? featureCards : []).map((it) =>
    it && it.json_data && typeof it.json_data === "object"
      ? { ...it, ...it.json_data }
      : it,
  );
}

function listingMessageFor(cls, fallbackText) {
  if (cls?.listing_message) return cls.listing_message;
  return fallbackText || AVAILABLE_WINES_MESSAGE;
}

// Render a non-text (is_text_output:false) classification: the intro line as
// a normal bot bubble, then the typed feature cards as a carousel message.
// Unmapped or not-yet-implemented types degrade to intro-text-only (the
// carousel logs a warning and renders nothing if no renderer is registered).
function emitFeatureCards(cls, listingMessage) {
  const intro = cls.introductory_message;
  if (intro) {
    addMessage({ role: "bot", kind: "text", data: { text: intro } });
  }

  // Once the intro claims the turn's scroll anchor, everything after it in
  // this turn must opt out (isFirstResponse:false) — MessageList now scrolls
  // straight to whatever message triggers it, so a second isFirstResponse:true
  // message would re-scroll and hide the intro instead of just no-op'ing
  // (same convention fetchRecommendations already uses below for its own
  // messages). With no intro, the cards themselves are the turn's content,
  // so they keep the default (true).
  const isFirstResponse = !intro;

  const items = flattenFeatureCards(cls.feature_cards);

  // Cart is a stacked panel, not a carousel — emit a 'cart' message that the
  // CartPanel renderer (registered in messages/index.js) consumes.
  if (cls.type === "cart_details") {
    const cartData = cls.cart_details || {};
    const cartItems = items.length ? items : (cartData.items || []);
    if (cartItems.length) {
      addMessage({
        role: "bot",
        kind: "cart",
        isFirstResponse,
        data: { items: cartItems, cart: cartData },
      });
    }
    return; // intro line above already states empty / success
  }

  const cardType = TYPE_TO_CARD[cls.type];

  if (cardType && items.length) {
    const data = { cardType, items };
    // ⚠ SHARED/BASE — wine Shop Our Wines pagination meta for CardCarousel.
    if (cardType === "wine") {
      data.pagination = cls.pagination || null;
      data.listingType = cls.type || "wine_listing";
      data.listingMessage = listingMessageFor(cls, listingMessage);
      if (cls.food_pairings_task_id) {
        data.foodPairingsTaskId = cls.food_pairings_task_id;
        // Persist for wine drawer — cards may not retain carousel dataset after re-render.
        setState({
          session: {
            ...getState().session,
            foodPairingsTaskId: cls.food_pairings_task_id,
          },
        });
      }
    }
    addMessage({
      role: "bot",
      kind: "cards",
      isFirstResponse,
      data,
    });
    // Parity: wine listings get a "View cart / Proceed to checkout" row under
    // the carousel when the cart feature is on (old createWineListing footer).
    // Never the turn's anchor — it's a footer under the cards, not the content.
    if (cardType === "wine" && getUiFeature("show_cart", false)) {
      addMessage({ role: "bot", kind: "cart-actions", isFirstResponse: false, data: {} });
    }
  } else if (!intro && !items.length) {
    // Nothing classifiable to show — avoid a silent dead turn.
    addMessage({ role: "bot", kind: "text", data: { text: errorText() } });
  }
}

async function handleRequest(
  text,
  source,
  usedStt,
  uniqueId,
  pageNo,
  quizStatus = "",
  userFullName = "",
  userEmail = "",
  drawerContext = null,
) {
  if (!text && !userFullName && !userEmail) return;
  if (getState().chat.isStreaming) return;

  const sessionQuiz = getState().quiz || {};
  // Old main.js: clicking "Take the wine quiz" always sends quiz_status=start_quiz.
  // Do not fall back to in_progress from a raced quiz-state update.
  let effectiveQuizStatus = quizStatus || sessionQuiz.status || "";
  if (text === "Take the wine quiz") {
    effectiveQuizStatus = "start_quiz";
  } else if (text === "submitting-quiz" || (userFullName && userEmail)) {
    effectiveQuizStatus = "completed";
  }

  updateChatState({ isStreaming: true, isDisabled: true });
  // scroll:true — show 3-dot loader + pin to bottom immediately on click
  // (Shop Our Wines / prompts / input), before get-response-type returns.
  // Do not use scroll on fetchRecommendations typing (keeps card scroll).
  emit("ws-chat-typing", { active: true, scroll: true });
  try {
    const sessionId = await ensureSession();
    if (!sessionId) throw new Error("No chat session");

    const isQuizSubmit =
      text === "submitting-quiz" || (!!userFullName && !!userEmail);
    const isSearch = source === "search";
    const buttonClicked = !isQuizSubmit && (source === "prompt" || source === "pagination");
    const params = {
      session_id: sessionId,
      // Old main.js: sendMessage("submitting-quiz", …) → user_message="" on the wire.
      user_message: isQuizSubmit ? "" : text,
      button_clicked: buttonClicked,
      clicked_on_guided_flow: !isQuizSubmit && (source === "prompt" || source === "pagination"),
      is_user_authenticated: getState().user?.isAuthenticated ?? false,
      used_stt: !!usedStt,
      is_search: isSearch,
      page_no: pageNo || 1,
    };
    if (uniqueId !== undefined && uniqueId !== null && uniqueId !== "") {
      params.unique_id = String(uniqueId);
    }
    if (drawerContext) {
      params.unique_id = params.unique_id || String(drawerContext.uniqueId || "");
      params.drawer_tab = drawerContext.tab || "";
      if (drawerContext.foodPairingsTaskId) {
        params.food_pairings_task_id = drawerContext.foodPairingsTaskId;
      }
      if (drawerContext.recipeTitle) {
        params.drawer_recipe_title = drawerContext.recipeTitle;
      }
      if (drawerContext.wineKey) {
        params.drawer_wine_key = drawerContext.wineKey;
      }
    }
    const cartId = getState().cart?.id;
    if (cartId) params.cartId = cartId;
    if (effectiveQuizStatus) params.quiz_status = effectiveQuizStatus;
    if (userFullName) params.user_full_name = userFullName;
    if (userEmail) params.user_email = userEmail;

    const cls = await api.getResponseType(params);

    let skipRecommendations = false;
    if (cls && cls.type === "quiz") {
      emit("ws-chat-typing", { active: false });
      emit("ws-quiz-response", { cls });
      skipRecommendations = true;
    } else if (cls && cls.is_text_output === false) {
      // Cards render immediately — hide the dots first so they don't sit above them.
      emit("ws-chat-typing", { active: false });
      emitFeatureCards(cls, text);
      // Parity with old main.js: skip follow-ups for reviews / cart, and for
      // wineengine also skip after wine listings + membership (Shop Our Wines /
      // Build Wine Box → no question-recommendations call).
      skipRecommendations = shouldSkipRecommendations(cls, text);
    } else {
      // Stream path: keep the typing indicator running until the first token
      // arrives (streamReply hides it then). The first byte can be many seconds
      // out, so hiding it here would leave a blank, dead-looking UI.
      // Shared lifecycle signal; variants may enhance a slow text response.
      // Classification, feature cards, and recommendations do not start it.
      emit("ws-chat-stream-waiting", { active: true });
      try {
        await streamReply(sessionId, text, buttonClicked, usedStt, isSearch);
      } finally {
        emit("ws-chat-stream-waiting", { active: false });
      }
    }
    emit("ws-chat-history-checkpoint", { sessionId });
    if (!skipRecommendations) await fetchRecommendations(sessionId);
  } catch (err) {
    console.error("[RedConcierge] chat request failed", err);
    addMessage({ role: "bot", kind: "text", data: { text: errorText() } });
  } finally {
    emit("ws-chat-typing", { active: false }); // safety: also clears on the error path
    updateChatState({ isStreaming: false, isDisabled: false });
    emit("ws-chat-settled", { sessionId: getState().session?.id ?? null });
  }
}

// In-place wine page swap (old loadAvailableWinePage). Replaces the existing
// cards message instead of appending a new carousel + intro.
async function handleWinePage({
  pageNo,
  messageId,
  listingMessage,
  foodPairingsTaskId,
  listingEl,
}) {
  if (!pageNo || !messageId) return;

  const clearLoader = () => {
    if (!listingEl) return;
    const loader = listingEl.querySelector(".ws-wine-pagination-loader");
    listingEl.classList.remove("ws-wine-listing-loading");
    if (loader) loader.remove();
    listingEl.querySelectorAll("[data-wine-page]").forEach((btn) => {
      btn.disabled = btn.classList.contains("active");
    });
  };

  if (getState().chat.isStreaming) {
    clearLoader();
    return;
  }

  const text = listingMessage || AVAILABLE_WINES_MESSAGE;
  updateChatState({ isStreaming: true });

  try {
    const sessionId = await ensureSession();
    if (!sessionId) throw new Error("No chat session");

    const params = {
      session_id: sessionId,
      user_message: text,
      button_clicked: true,
      clicked_on_guided_flow: true,
      is_user_authenticated: getState().user?.isAuthenticated ?? false,
      used_stt: false,
      is_search: false,
      page_no: pageNo,
    };
    if (foodPairingsTaskId) {
      params.food_pairings_task_id = foodPairingsTaskId;
    }

    const cls = await api.getResponseType(params);
    if (!cls || cls.is_text_output !== false) {
      throw new Error("Unexpected wine page response");
    }

    const cardType = TYPE_TO_CARD[cls.type];
    if (cardType !== "wine") {
      throw new Error("Unexpected wine page response type");
    }

    const items = flattenFeatureCards(cls.feature_cards);
    updateMessage(messageId, {
      cardType: "wine",
      items,
      pagination: cls.pagination || null,
      listingType: cls.type || "wine_listing",
      listingMessage: listingMessageFor(cls, text),
      foodPairingsTaskId: cls.food_pairings_task_id || foodPairingsTaskId || "",
    });
    const taskId = cls.food_pairings_task_id || foodPairingsTaskId;
    if (taskId) {
      setState({
        session: { ...getState().session, foodPairingsTaskId: taskId },
      });
    }
    emit("ws-chat-history-checkpoint", { sessionId });
  } catch (err) {
    console.error("[RedConcierge] wine page load failed", err);
    clearLoader();
  } finally {
    updateChatState({ isStreaming: false });
    emit("ws-chat-settled", { sessionId: getState().session?.id ?? null });
  }
}

export async function init() {
  on("ws-chat-request", ({
    text,
    source,
    usedStt,
    uniqueId,
    quizStatus,
    userFullName,
    userEmail,
    drawerContext,
  }) =>
    handleRequest(text, source, usedStt, uniqueId, undefined, quizStatus, userFullName, userEmail, drawerContext),
  );

  // ⚠ SHARED/BASE — Shop Our Wines pagination for every winery.
  on("ws-wine-page", (payload) => handleWinePage(payload));

  // Create / restore the session the first time the panel opens.
  on("ws-toggle", ({ isOpen }) => {
    if (isOpen) ensureSession();
  });

  console.info("[RedConcierge] Chat handler initialized");
}
